import {ReactElement, ReactNode, useState, MouseEvent} from 'react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {Typography, Collapse, IconButton, styled} from '@mui/material';
import {Link} from '@tanstack/react-router';
import {TFunction} from 'i18next';

const StyledNavLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'onlyIcon'
})(({theme, onlyIcon}: any) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: onlyIcon ? 'center' : 'flex-start',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  textDecoration: 'none',
  fontSize: '18px',
  color: theme.palette.grey[600],
  '&.active .MuiTypography-root': {
    fontWeight: 'bold',
    color: theme.palette.primary.main
  },
  '&::before': {
    display: 'none',
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    background: `linear-gradient(to right, ${theme.palette.secondary.light}ee, ${theme.palette.secondary.main})`,
    content: '""',
    opacity: 0.25,
    padding: '30px 30px',
    borderTopRightRadius: '30px',
    borderBottomRightRadius: '30px'
  },
  '&.active::before': {
    display: 'block'
  },
  '& > svg': {
    marginRight: onlyIcon ? 0 : theme.spacing(2)
  },
  '&.active > svg': {
    color: theme.palette.secondary.main
  }
}));

export type MenuItemType = {
  name: string | ReactNode;
  link: string;
  icon: ReactNode;
  id?: string;
  children?: MenuItemType[];
};

type Props = {
  onlyIcon?: boolean;
  menuItem: MenuItemType;
  onClick?: () => void;
  onCloseSidebar?: () => void;
  t: TFunction;
};

function MenuItem({onlyIcon = false, menuItem, onClick, t}: Props): ReactElement {
  const [isSubMenuOpened, setIsSubMenuOpened] = useState(false);

  const {link, icon, name, id, children} = menuItem;

  function handleClickOnExpand(event: MouseEvent) {
    event.preventDefault();
    setIsSubMenuOpened((value) => !value);
  }

  return (
    <>
      <StyledNavLink id={id} to={link} onClick={onClick} activeProps={{className: 'active'}}>
        {icon}
        {!onlyIcon && (
          <Typography
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexGrow: 1,
              pr: 1
            }}
            variant="body1"
            component="div"
          >
            {typeof name === 'string' ? t(name) : name}
            {children && (
              <IconButton
                onClick={handleClickOnExpand}
                color="primary"
                aria-label="expand"
                component="span"
                sx={{
                  p: 0
                }}
                size="large"
              >
                {isSubMenuOpened ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </Typography>
        )}
      </StyledNavLink>
      {children && (
        <Collapse
          sx={{
            ml: 4
          }}
          in={isSubMenuOpened}
          timeout="auto"
          unmountOnExit
        >
          {children.map((subMenuItem) => (
            <MenuItem key={subMenuItem.link} menuItem={subMenuItem} t={t} />
          ))}
        </Collapse>
      )}
    </>
  );
}

export default MenuItem;
